/**
 * Retry Mechanism with Exponential Backoff for Supabase Operations
 * Handles transient failures with configurable thresholds and fallback states
 */

import { errorLogger } from './errorLogger';

// Retry configuration
export const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  exponentialMultiplier: 2,
  jitterMax: 1000, // Random 0-1000ms added to delay
  retryableStatusCodes: [429, 500, 502, 503, 504],
  retryableErrorCodes: [
    'ETIMEDOUT',
    'ECONNRESET',
    'ECONNREFUSED',
    'ENOTFOUND',
    'ENETUNREACH',
    'EAI_AGAIN',
  ],
};

// Circuit breaker configuration
const CIRCUIT_BREAKER_CONFIG = {
  failureThreshold: 5, // Number of consecutive failures before opening circuit
  resetTimeout: 60000, // 1 minute before attempting to close circuit
  halfOpenMaxAttempts: 3, // Max attempts in half-open state
};

// Circuit breaker states
const CIRCUIT_STATES = {
  CLOSED: 'closed', // Normal operation
  OPEN: 'open', // Circuit is open, requests fail immediately
  HALF_OPEN: 'half_open', // Testing if service recovered
};

/**
 * Circuit Breaker implementation
 */
class CircuitBreaker {
  constructor(config = CIRCUIT_BREAKER_CONFIG) {
    this.config = config;
    this.state = CIRCUIT_STATES?.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.nextAttemptTime = null;
    this.halfOpenAttempts = 0;
  }

  /**
   * Check if request should be allowed
   */
  canAttempt() {
    if (this.state === CIRCUIT_STATES?.CLOSED) {
      return true;
    }

    if (this.state === CIRCUIT_STATES?.OPEN) {
      // Check if enough time has passed to try half-open
      if (Date.now() >= this.nextAttemptTime) {
        this.state = CIRCUIT_STATES?.HALF_OPEN;
        this.halfOpenAttempts = 0;
        console.log('[CircuitBreaker] Transitioning to HALF_OPEN state');
        return true;
      }
      return false;
    }

    if (this.state === CIRCUIT_STATES?.HALF_OPEN) {
      return this.halfOpenAttempts < this.config?.halfOpenMaxAttempts;
    }

    return false;
  }

  /**
   * Record successful operation
   */
  recordSuccess() {
    this.failureCount = 0;

    if (this.state === CIRCUIT_STATES?.HALF_OPEN) {
      this.successCount++;
      if (this.successCount >= this.config?.halfOpenMaxAttempts) {
        this.state = CIRCUIT_STATES?.CLOSED;
        this.successCount = 0;
        this.halfOpenAttempts = 0;
        console.log('[CircuitBreaker] Circuit CLOSED - service recovered');
      }
    }
  }

  /**
   * Record failed operation
   */
  recordFailure() {
    this.failureCount++;

    if (this.state === CIRCUIT_STATES?.HALF_OPEN) {
      this.halfOpenAttempts++;
      if (this.halfOpenAttempts >= this.config?.halfOpenMaxAttempts) {
        this.openCircuit();
      }
    } else if (this.state === CIRCUIT_STATES?.CLOSED) {
      if (this.failureCount >= this.config?.failureThreshold) {
        this.openCircuit();
      }
    }
  }

  /**
   * Open the circuit
   */
  openCircuit() {
    this.state = CIRCUIT_STATES?.OPEN;
    this.nextAttemptTime = Date.now() + this.config?.resetTimeout;
    console.warn(
      `[CircuitBreaker] Circuit OPEN - too many failures. Will retry at ${new Date(
        this.nextAttemptTime
      )?.toLocaleTimeString()}`
    );

    // Log circuit breaker activation
    errorLogger?.logError(
      new Error('Circuit breaker opened due to consecutive failures'),
      {
        component: 'CircuitBreaker',
        failureCount: this.failureCount,
        nextAttemptTime: this.nextAttemptTime,
      }
    );
  }

  /**
   * Get current circuit state
   */
  getState() {
    return this.state;
  }

  /**
   * Reset circuit breaker
   */
  reset() {
    this.state = CIRCUIT_STATES?.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.nextAttemptTime = null;
    this.halfOpenAttempts = 0;
    console.log('[CircuitBreaker] Circuit manually reset');
  }
}

// Global circuit breaker instance
const circuitBreaker = new CircuitBreaker();

/**
 * Check if error is transient and retryable
 */
export const isTransientError = (error) => {
  if (!error) return false;

  // Network errors
  if (error?.code && RETRY_CONFIG?.retryableErrorCodes?.includes(error?.code)) {
    return true;
  }

  // HTTP status codes
  if (error?.status && RETRY_CONFIG?.retryableStatusCodes?.includes(error?.status)) {
    return true;
  }

  // Supabase-specific transient errors
  if (error?.message) {
    const message = error?.message?.toLowerCase();
    if (
      message?.includes('network') ||
      message?.includes('timeout') ||
      message?.includes('connection') ||
      message?.includes('rate limit') ||
      message?.includes('temporarily unavailable') ||
      message?.includes('service unavailable')
    ) {
      return true;
    }
  }

  // Fetch API errors
  if (error?.name === 'NetworkError' || error?.name === 'TimeoutError') {
    return true;
  }

  return false;
};

/**
 * Calculate delay with exponential backoff and jitter
 */
export const calculateDelay = (attemptNumber, config = RETRY_CONFIG) => {
  const exponentialDelay = config?.initialDelay * Math.pow(config?.exponentialMultiplier, attemptNumber);
  const cappedDelay = Math.min(exponentialDelay, config?.maxDelay);
  const jitter = Math.random() * config?.jitterMax;
  return cappedDelay + jitter;
};

/**
 * Sleep for specified milliseconds
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retry operation with exponential backoff
 */
export const retryWithBackoff = async (
  operation,
  options = {},
  onRetry = null
) => {
  const config = { ...RETRY_CONFIG, ...options };
  let lastError = null;
  let attempt = 0;

  // Check circuit breaker
  if (!circuitBreaker?.canAttempt()) {
    const error = new Error('Circuit breaker is open - service temporarily unavailable');
    error.circuitOpen = true;
    error.circuitState = circuitBreaker?.getState();
    throw error;
  }

  while (attempt <= config?.maxRetries) {
    try {
      const result = await operation();
      
      // Record success in circuit breaker
      circuitBreaker?.recordSuccess();
      
      return result;
    } catch (error) {
      lastError = error;
      attempt++;

      // Check if error is retryable
      if (!isTransientError(error)) {
        console.error('[Retry] Non-retryable error encountered:', error);
        circuitBreaker?.recordFailure();
        throw error;
      }

      // If max retries reached, throw error
      if (attempt > config?.maxRetries) {
        console.error(`[Retry] Max retries (${config?.maxRetries}) exceeded`);
        circuitBreaker?.recordFailure();
        
        // Log retry exhaustion
        errorLogger?.logError(error, {
          component: 'RetryMechanism',
          maxRetries: config?.maxRetries,
          finalAttempt: attempt,
          errorType: 'retry_exhausted',
        });
        
        throw error;
      }

      // Calculate delay and wait
      const delay = calculateDelay(attempt - 1, config);
      console.warn(
        `[Retry] Attempt ${attempt}/${config?.maxRetries} failed. Retrying in ${Math.round(
          delay
        )}ms...`,
        error?.message
      );

      // Notify retry callback
      if (onRetry) {
        onRetry(attempt, config?.maxRetries, delay, error);
      }

      // Log retry attempt
      errorLogger?.logError(error, {
        component: 'RetryMechanism',
        attempt,
        maxRetries: config?.maxRetries,
        delay,
        errorType: 'transient_error',
      });

      await sleep(delay);
    }
  }

  // Should never reach here, but throw last error as fallback
  throw lastError;
};

/**
 * Get circuit breaker instance
 */
export const getCircuitBreaker = () => circuitBreaker;

/**
 * Reset circuit breaker (useful for manual recovery)
 */
export const resetCircuitBreaker = () => {
  circuitBreaker?.reset();
};

/**
 * Get circuit breaker state
 */
export const getCircuitBreakerState = () => ({
  state: circuitBreaker?.getState(),
  failureCount: circuitBreaker?.failureCount,
  canAttempt: circuitBreaker?.canAttempt(),
});
